import { Dispatch } from 'redux'

import { useThunkActions } from 'redux/actions/useThunkActions'

import { ApplicationState } from 'redux/Stores/types'
import {
  accept,
  destroy,
  getAll,
  getOne,
  update,
  updateSelection
} from 'redux/api/Analyses'
import { tryRefetchAllTasks } from 'redux/actions/Tasks'

import type {
  TAcceptAnalysisTask,
  TUpdateAnalysisRequestBody,
  TUpdateAnalysisSelectionRequestBody
} from '@homepal/api-contract'

import { EAnalysisStatus } from 'types/Enums'
import { SnackbarMessage } from 'types/GlobalSnackbar'

import { toggleSnackbarOpen } from '../Snackbar'
import * as actions from './actions'

export function tryGetAllAnalyses() {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const { fetchStatus } = getState().AnalysisStore

    if (fetchStatus === 'fetching' || fetchStatus === 'success') {
      return
    }

    dispatch(actions.getAll())

    getAll()
      .then((response) => {
        dispatch(actions.getAllSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getAllFailed())
      })
  }
}

// Fetches a single analysis to obtain a fresh signed html url (the bulk list
// omits it, and signed urls are short-lived).
function tryGetOneAnalysis(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.getOne())

    getOne(id)
      .then((response) => {
        dispatch(actions.getOneSuccess(response.data))
      })
      .catch(() => {
        dispatch(actions.getOneFailed())
      })
  }
}

function tryUpdateAnalysis(
  id: string,
  body: TUpdateAnalysisRequestBody<EAnalysisStatus>,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, body)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.updateFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))

        callback?.(true)
      })
  }
}

// Creates the activities and moves the analysis to accepted in one call, so a
// failure cannot leave activities behind on an unhandled analysis. The task
// list is refetched because the response carries the analysis alone.
function tryAcceptAnalysis(
  id: string,
  tasks: TAcceptAnalysisTask[],
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    accept(id, tasks)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))

        tryRefetchAllTasks()(dispatch)

        callback?.()
      })
      .catch(() => {
        dispatch(actions.updateFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))

        callback?.(true)
      })
  }
}

function tryUpdateAnalysisSelection(
  id: string,
  names: TUpdateAnalysisSelectionRequestBody
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.updateSelection())

    updateSelection(id, names)
      .then((response) => {
        dispatch(actions.updateSelectionSuccess(response.data.data))
        dispatch(toggleSnackbarOpen(SnackbarMessage.Success))
      })
      .catch(() => {
        dispatch(actions.updateSelectionFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.Failed))
      })
  }
}

function tryDeleteAnalysis(id: string, callback?: (err?: boolean) => void) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess(id))
        dispatch(toggleSnackbarOpen(SnackbarMessage.AnalysisDeleted))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.destroyFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.AnalysisDeletedFailed))

        callback?.(true)
      })
  }
}

export const useAnalysisActions = () => {
  return {
    tryGetAllAnalyses: useThunkActions(tryGetAllAnalyses),
    tryGetOneAnalysis: useThunkActions(tryGetOneAnalysis),
    tryUpdateAnalysis: useThunkActions(tryUpdateAnalysis),
    tryAcceptAnalysis: useThunkActions(tryAcceptAnalysis),
    tryDeleteAnalysis: useThunkActions(tryDeleteAnalysis),
    tryUpdateAnalysisSelection: useThunkActions(tryUpdateAnalysisSelection)
  }
}
